/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/reports';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Custom square bar component
 * @param {any} properties - The properties
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => {}}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </>
  );
}

/**
 * ChargerReportRevenue component
 */
function ChargerReportRevenue() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
  /**
   * Custom tooltip
   * @param {any} properties - The properties
   * @returns {any} The tooltip
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label || data?.stationName}</p>
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>
              {t('idle')}: {data?.idle}hrs
            </p>
            <p style={{ color: '#2E9E3A' }}>
              {t('inUse')}: {data?.inuse}hrs
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The time period for which to fetch the report.
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'uptimeVsInuse',
        accountCustomId: reportsData?.accountId,
      });
      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };
  /**
   * If clicking the already active pin, deactivate it
   * @param {any} name - The name
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('uptimeVsinuse') ? 'active' : ''}`}
              onClick={() => handleClick('uptimeVsinuse')}>
              {reportsData.activePinName.includes('uptimeVsinuse') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>

            <p>
              {t('chargerUtilizationIdle')} {t('vs')} {t('inUse')}{' '}
            </p>
          </div>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(start, end) => {
              setDateRange({ startDate: start, endDate: end });
              fetchPowerConsumedReport('last24hours');
            }}
            placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          />
        </div>
        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData}>
              <XAxis
                dataKey="label" // Assuming 'label' has 'Jan', 'Feb', etc.
                axisLine
                tickLine={false}
                tick={{ fontSize: 12 }}
              />

              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar dataKey="idle" name="Idle" fill="#4ABDFB" shape={<TriangleBar />} barSize={5} />
              <Bar
                dataKey="inuse"
                name="In use"
                fill="#2AA700"
                shape={<TriangleBar />}
                barSize={5}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="energy-report-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#4ABDFB' }} />
          <p>{t('idle')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2AA700' }} />
          <p>{t('inUse')}</p>
        </div>
      </div>
    </div>
  );
}

export default ChargerReportRevenue;
